import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Quote, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";

const TestimonialSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  const testimonials = [
    {
      id: 1,
      name: "Rajesh Kumar",
      position: "CTO, HealthTech Solutions",
      company: "HealthTech Solutions Pvt Ltd",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f2277bfd-1763295470977.png",
      imageAlt: "Professional headshot of Indian male executive",
      testimonial:
        "Orator Hype transformed our entire IT infrastructure while simultaneously elevating our brand presence. Their dual expertise in technology and branding is unmatched.",
    },
    {
      id: 2,
      name: "Priya Sharma",
      position: "Founder & CEO",
      company: "EduTech Innovations",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_12272604c-1763300454529.png",
      imageAlt: "Professional headshot of Indian female entrepreneur",
      testimonial:
        "Working with Orator Hype was a game-changer for our startup. They crafted our entire digital identity and provided unmatched support.",
    },
    {
      id: 3,
      name: "Amit Patel",
      position: "IT Director",
      company: "Global Manufacturing Solutions",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1866265bd-1763293085876.png",
      imageAlt: "Professional headshot of Indian male IT director",
      testimonial:
        "Our legacy systems are now fully cloud-enabled. The transition was seamless and professionally executed.",
    },
    {
  id: 4,
  name: "Priya Sharma",
  position: "Digital Marketing Manager",
  company: "Urban Retail Group",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_2866265bd-1763293085877.png",
  imageAlt: "Professional headshot of Indian female marketing manager",
  testimonial:
    "Our digital transformation was accelerated by 6 months with Orator's integrated solutions. Exceptional work from strategy to execution.",
},

{
  id: 5,
  name: "Rahul Verma",
  position: "Operations Head",
  company: "FinTech Innovations Pvt. Ltd.",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_3866265bd-1763293085878.png",
  imageAlt: "Professional headshot of Indian male operations head",
  testimonial:
    "The cybersecurity framework implemented has protected us from 3 major threats this quarter alone. Truly proactive security partners.",
},

{
  id: 6,
  name: "Sneha Reddy",
  position: "Brand Director",
  company: "Luxe Lifestyle Brands",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_4866265bd-1763293085879.png",
  imageAlt: "Professional headshot of Indian female brand director",
  testimonial:
    "Our brand identity was completely revitalized. The creative strategy resonated perfectly with our target audience across 8 cities.",
},

{
  id: 7,
  name: "Vikram Singh",
  position: "CTO",
  company: "HealthTech Ventures",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_5866265bd-1763293085880.png",
  imageAlt: "Professional headshot of Indian male CTO",
  testimonial:
    "Their cloud migration strategy saved us 40% in infrastructure costs while improving system reliability by 70%. Outstanding ROI.",
},

{
  id: 8,
  name: "Ananya Chatterjee",
  position: "PR & Communications Head",
  company: "Cultural Events Collective",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_6866265bd-1763293085881.png",
  imageAlt: "Professional headshot of Indian female PR head",
  testimonial:
    "Media coverage increased by 300% after implementing their PR strategy. The team's industry connections are impressive.",
}
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      handleNext();
    }, 4000);
    return () => clearInterval(interval);
  }, [currentIndex]);

  const handleNext = () => {
    setDirection(1);
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const handlePrev = () => {
    setDirection(-1);
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 50 : -50,
      opacity: 0,
      scale: 0.95,
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1,
      transition: {
        x: { duration: 0.3 },
        opacity: { duration: 0.3 },
        scale: { duration: 0.2 },
      }
    },
    exit: (direction) => ({
      x: direction < 0 ? 50 : -50,
      opacity: 0,
      scale: 0.95,
      transition: {
        x: { duration: 0.3 },
        opacity: { duration: 0.2 },
      }
    })
  };

  const current = testimonials[currentIndex];

  return (
    <section className="relative py-16 lg:py-20 overflow-hidden bg-gradient-to-br from-[#2642B2] via-indigo-900 to-slate-900">
      {/* Background particles */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-10 left-5 w-16 h-16 bg-white/5 backdrop-blur-sm rounded-full animate-float-slow"></div>
        <div className="absolute top-1/2 right-8 w-12 h-12 bg-cyan-400/10 backdrop-blur-sm rounded-full animate-float"></div>
        <div className="absolute bottom-16 left-1/4 w-20 h-20 bg-purple-500/10 backdrop-blur-sm rounded-full animate-float-slow delay-1000"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Compact Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-4">
            <Sparkles className="w-3 h-3 text-cyan-400" />
            <span className="text-xs font-semibold text-white tracking-wider">CLIENT TESTIMONIALS</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
            What Our{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-white">
              Clients Say
            </span>
          </h2>
          <p className="text-gray-300 text-sm max-w-lg mx-auto">
            Trusted by industry leaders for digital transformation
          </p>
        </div>

        {/* Compact Testimonial Card */}
        <div className="max-w-4xl mx-auto">
          <div className="relative">
            {/* Navigation Arrows */}
            <div className="absolute -top-16 right-4 flex gap-2 z-20">
              <button
                onClick={handlePrev}
                className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center hover:bg-white/20 transition-all duration-300 group"
              >
                <ChevronLeft className="w-5 h-5 text-white group-hover:text-cyan-400 transition-colors" />
              </button>
              <button
                onClick={handleNext}
                className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center hover:bg-white/20 transition-all duration-300 group"
              >
                <ChevronRight className="w-5 h-5 text-white group-hover:text-cyan-400 transition-colors" />
              </button>
            </div>

            {/* Main Card */}
            <div className="relative bg-white/5 backdrop-blur-xl border border-white/20 rounded-2xl p-8 md:p-10 shadow-2xl">
              {/* Gradient glow */}
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-400/10 via-blue-400/5 to-purple-400/10 rounded-2xl blur-xl -inset-1"></div>

              <div className="relative z-10">
                <AnimatePresence mode="wait" custom={direction}>
                  <motion.div
                    key={currentIndex}
                    custom={direction}
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    className="flex flex-col lg:flex-row items-center lg:items-start gap-8"
                  >
                    {/* Profile Section */}
                    <div className="flex flex-col items-center lg:items-start gap-4 flex-shrink-0">
                      <div className="relative group">
                        <div className="w-24 h-24 md:w-28 md:h-28 rounded-2xl border-4 border-white/20 shadow-xl overflow-hidden ring-2 ring-transparent group-hover:ring-cyan-500/30 transition-all duration-300">
                          <motion.img
                            src={current.image}
                            alt={current.imageAlt}
                            className="w-full h-full object-cover"
                            initial={{ scale: 1.1 }}
                            animate={{ scale: 1 }}
                            transition={{ duration: 0.5 }}
                          />
                        </div>
                        
                        {/* Quote icon */}
                        <div className="absolute -bottom-3 -right-3 w-12 h-12 bg-gradient-to-br from-[#2642B2] to-indigo-700 rounded-xl flex items-center justify-center shadow-lg border-2 border-white/20">
                          <Quote className="w-5 h-5 text-white" />
                        </div>
                      </div>

                      {/* Profile Info */}
                      <div className="text-center lg:text-left space-y-1">
                        <h3 className="text-xl font-bold text-white">
                          {current.name}
                        </h3>
                        <p className="text-cyan-100/90 font-medium text-sm">
                          {current.position}
                        </p>
                        <p className="text-white/80 font-semibold text-xs">
                          {current.company}
                        </p>
                      </div>
                    </div>

                    {/* Testimonial Quote */}
                    <div className="flex-1 lg:pl-8 border-l-0 lg:border-l border-white/10">
                      <div className="relative">
                        <p className="text-lg md:text-xl text-white/95 leading-relaxed italic pl-0 lg:pl-6">
                          "{current.testimonial}"
                        </p>
                        
                        {/* Quote mark */}
                        <div className="hidden lg:block absolute -top-2 -left-2 w-8 h-8 bg-gradient-to-br from-cyan-400/20 to-blue-400/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                          <span className="text-lg font-serif font-black text-white/80">"</span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Progress Dots */}
              <div className="flex justify-center gap-2 mt-8 pt-6 border-t border-white/10">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className="relative"
                  >
                    <div className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      index === currentIndex 
                        ? 'bg-gradient-to-r from-cyan-400 to-blue-400' 
                        : 'bg-white/40 hover:bg-white/60'
                    }`} />
                    
                    {index === currentIndex && (
                      <motion.div
                        layoutId="activeDot"
                        className="absolute -inset-1 rounded-full border border-cyan-400/30"
                      />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Animations */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-6px); }
        }
        
        @keyframes float-slow {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
        
        .animate-float {
          animation: float 4s ease-in-out infinite;
        }
        
        .animate-float-slow {
          animation: float-slow 6s ease-in-out infinite;
        }
        
        .delay-1000 { animation-delay: 1s; }
      `}</style>
    </section>
  );
};

export default TestimonialSection;